import { z } from 'zod';

import { AlertStatus } from '@/entities/alert-status';
import {
  codec,
  useQueryParams,
  type UseQueryParamsResult,
} from '@/hooks/router/use-query-params';

import { PCSAlgorithm } from '../entities/pcs-algorithm';
import type { PCSOverview } from '../entities/pcs-overview';
import type { TimeseriesMetric } from '../entities/pcs-timeseries';

import type { RequestOptions } from './utils/request-options';

export namespace IPCSRepository {
  export type GetAllInput = { filters?: Filters; assetFullFrontendId: string };
  export type GetAllOutput = PCSOverview[];

  export type GetPowerAvailabilityInput = {
    filters?: Filters;
    assetFullFrontendId: string;
  };
  export type GetPowerAvailabilityOutput = TimeseriesMetric;

  export type GetPowerCapabilityInput = {
    filters?: Filters;
    assetFullFrontendId: string;
  };
  export type GetPowerCapabilityOutput = TimeseriesMetric;

  export type GetPowerEfficiencyInput = {
    filters?: Filters;
    assetFullFrontendId: string;
  };
  export type GetPowerEfficiencyOutput = {
    powerEfficiencyDCH: TimeseriesMetric;
    powerEfficiencyCHA: TimeseriesMetric;
  };

  export type GetAlgorithmStatusInput = {
    algorithm: PCSAlgorithm;
    filters?: Filters;
    assetFullFrontendId: string;
  };
  export type GetAlgorithmStatusOutput = AlertStatus;

  export type Filters = {
    pcsId?: string;
  };

  export const useGetFiltersFromQueryParams =
    (): UseQueryParamsResult<Filters> =>
      useQueryParams({
        pcsId: {
          key: 'pcs',
          schema: z.string().optional(),
          codec: codec.string,
        },
      });
}

export interface IPCSRepository {
  key: string;
  getAll(
    input: IPCSRepository.GetAllInput,
    requestOptions?: RequestOptions,
  ): Promise<IPCSRepository.GetAllOutput>;
  getPowerAvailability(
    input: IPCSRepository.GetPowerAvailabilityInput,
    requestOptions?: RequestOptions,
  ): Promise<IPCSRepository.GetPowerAvailabilityOutput>;
  getPowerCapability(
    input: IPCSRepository.GetPowerCapabilityInput,
    requestOptions?: RequestOptions,
  ): Promise<IPCSRepository.GetPowerCapabilityOutput>;
  getPowerEfficiency(
    input: IPCSRepository.GetPowerEfficiencyInput,
    requestOptions?: RequestOptions,
  ): Promise<IPCSRepository.GetPowerEfficiencyOutput>;
  getAlgorithmStatus(
    input: IPCSRepository.GetAlgorithmStatusInput,
    requestOptions?: RequestOptions,
  ): Promise<IPCSRepository.GetAlgorithmStatusOutput>;
}
